import React, { useCallback, useEffect, useState, useMemo } from 'react';
import NewsResultCard from '../../components/NewsResultCard';
import { NewsItem } from './types';
import { Person } from '../people-listing/types';
import { FacetComponent } from '../../components/Facet';
import { SearchBoxComponent } from '../../components/SearchBox';
import { SelectedFiltersComponent } from '../../components/SelectedFilters';
import FilterDialog from '../../components/FilterDialog';
import ChevronUp from '../../../react-components/icons/ChevronUp';
import { PaginationComponent } from '../../components/Pagination';
import Summary from '../../components/Summary';
import Sort from '../../components/Sort';
import { Result } from '../../hooks/useCoveoSearch';
import { useCoveoListing } from '../../hooks/useCoveoListing';
import { useStructuredData } from '../../hooks/useStructuredData';
import {
  generateNewsItemListSchema,
  NewsSchema,
  StructuredDataConfig
} from '../../utils/structuredData';

const DEFAULT_DATE_FORMAT = 'MMM d, yyyy';

const NewsListing: React.FC = () => {
  const listingAriaLabel = 'News Listing';
  const [dateFormat, setDateFormat] = useState(DEFAULT_DATE_FORMAT);
  const [language, setLanguage] = useState('en');
  const [facetOptionsNumber, setFacetOptionsNumber] = useState(999);

  useEffect(() => {
    const sectionElement = document.querySelector(`[aria-label="${listingAriaLabel}"]`);
    if (sectionElement) {
      const df = sectionElement.getAttribute('data-date-format');
      const lang = sectionElement.getAttribute('data-language');
      const facetOptions = sectionElement.getAttribute('data-facet-options-number');
      setDateFormat(df || DEFAULT_DATE_FORMAT);
      setLanguage(lang ? lang.split('-')[0] : 'en');
      setFacetOptionsNumber(facetOptions ? Number(facetOptions) : 999);
    }
  }, []);

  const fallbackConfig = {
    organizationId: 'analysisgroupnonproduction13h6gxjnn',
    searchHub: 'euro-new-events',
    pipeline: 'euro-new-events',
    apiKey: 'xx8043e01e-0876-4045-bb89-d0902b725336',
    fieldsToInclude: [
      'title',
      'date',
      'source',
      'collection',
      'language',
      'contenttype',
      'eventenddate'
    ],
    facetFields: ['source', 'collection', 'language'],
    numberOfResults: 20
  }; // fallback config in case the data is not available in the DOM data-attributes

  const formatDate = useCallback(
    (timestamp: unknown, format: string, lang = 'en', endTimestamp?: unknown): string => {
      try {
        const getDate = (ts: unknown): Date | null => {
          if (typeof ts === 'number' || (typeof ts === 'string' && !isNaN(Number(ts)))) {
            return new Date(Number(ts));
          } else if (typeof ts === 'string') {
            return new Date(ts);
          }
          return null;
        };

        let date = getDate(timestamp);
        if (!date || isNaN(date.getTime())) {
          date = new Date(); // fallback
        }

        const endDate = endTimestamp ? getDate(endTimestamp) : null;

        const formatSingleDate = (d: Date, fmt: string) => {
          const day = d.getDate();
          const month = d.getMonth(); // 0-11
          const year = d.getFullYear();

          // Determine month format from input format string
          let monthStr: string;
          if (fmt.includes('MMMM')) {
            monthStr = new Intl.DateTimeFormat(lang, { month: 'long' }).format(d);
          } else if (fmt.includes('MMM')) {
            monthStr = new Intl.DateTimeFormat(lang, { month: 'short' }).format(d);
          } else if (fmt.includes('MM')) {
            monthStr = String(month + 1).padStart(2, '0');
          } else {
            monthStr = String(month + 1);
          }

          // Determine year format
          let yearStr: string;
          if (fmt.includes('yyyy')) {
            yearStr = String(year);
          } else if (fmt.includes('yy')) {
            yearStr = String(year).slice(-2);
          } else {
            yearStr = String(year);
          }

          // European format: day month year (e.g., "2 October 2025" or "2 Oct 2025")
          return `${day} ${monthStr} ${yearStr}`;
        };

        // Check if we have a valid end date that is different from start date
        if (endDate && !isNaN(endDate.getTime()) && endDate.getTime() > date.getTime()) {
          const startYear = date.getFullYear();
          const endYear = endDate.getFullYear();
          const startMonth = date.getMonth();
          const endMonth = endDate.getMonth();
          const startDay = date.getDate();
          const endDay = endDate.getDate();

          // Same day (shouldn't happen given the > check, but for safety)
          if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
            return formatSingleDate(date, format);
          }

          // Determine month format from the input format string
          const isLongMonth = format.includes('MMMM');
          const monthFormat = isLongMonth ? 'long' : 'short';
          const startMonthStr = new Intl.DateTimeFormat(lang, { month: monthFormat }).format(date);
          const endMonthStr = new Intl.DateTimeFormat(lang, { month: monthFormat }).format(endDate);

          // Same Month and Year: "2–3 October 2025" use en dash
          if (startYear === endYear && startMonth === endMonth) {
            return `${startDay}–${endDay} ${startMonthStr} ${startYear}`;
          }

          // Different Month, Same Year: "2 October–4 November 2025"
          if (startYear === endYear) {
            return `${startDay} ${startMonthStr}–${endDay} ${endMonthStr} ${startYear}`;
          }

          // Different Year: "2 October 2025–3 January 2026"
          return `${formatSingleDate(date, format)}–${formatSingleDate(endDate, format)}`;
        }

        return formatSingleDate(date, format);
      } catch (error) {
        console.warn('Error formatting date:', timestamp, error);
        return new Date().toLocaleDateString(lang);
      }
    },
    []
  );

  const transformCoveoResult = useCallback(
    (result: Result): NewsItem => {
      const r = result as unknown as { [key: string]: unknown };
      const raw = (r.raw as { [key: string]: unknown }) || {};

      const contentType = (raw.displaycontenttype ||
        raw.contenttype ||
        r.contenttype ||
        'NEWS') as string;

      // Use specific date fields based on content type
      const dateValue = raw.pageauthordate || raw.date;
      const endDateValue = raw.eventenddate || raw.enddate;
      const formattedDate = formatDate(dateValue, dateFormat, language, endDateValue);

      return {
        id: (r.uniqueId || r.id || String(Math.random())) as string,
        type: contentType,
        title: (raw.pagename || r.title || 'Untitled') as string,
        date: formattedDate,
        url: r.clickUri as string,
        // Keep reference to original result for analytics
        originalResult: result
      };
    },
    [formatDate, dateFormat, language]
  );

  const {
    listingSectionRef,
    isMobile,
    isFilterDialogOpen,
    setIsFilterDialogOpen,
    labels,
    searchLabels,
    coveoState,
    sortFields,
    displayedFacets,
    selectedFilterValues,
    transformedResults,
    numberOfResults,
    handleSearchSubmit,
    handleSortChange,
    handlePageChange,
    handleFacetChange,
    handleRemoveSelectedFilter,
    handleClearAllFilters
  } = useCoveoListing<NewsItem>({
    listingAriaLabel,
    transformCoveoResult,
    fallbackConfig,
    language
  });

  // Generate structured data for SEO
  const structuredDataConfig: StructuredDataConfig = useMemo(
    () => ({
      listName: 'News & Events Listing',
      description: 'A collection of news articles and events from Analysis Group.',
      url: typeof window !== 'undefined' ? window.location.href : undefined
    }),
    []
  );

  const structuredDataSchema = useMemo(() => {
    if (!transformedResults.length || coveoState.loading) {
      return null;
    }

    const newsSchema: NewsSchema[] = transformedResults.map(newsItem => {
      // Get additional data from the original Coveo results
      const coveoResult = coveoState.results?.find((result: Result) => {
        const r = result as unknown as { [key: string]: unknown };
        return r.uniqueId === newsItem.id || r.id === newsItem.id;
      });
      const raw = coveoResult
        ? ((coveoResult as unknown as { [key: string]: unknown }).raw as {
            [key: string]: unknown;
          }) || {}
        : {};

      return {
        id: newsItem.id,
        title: newsItem.title,
        datePublished: newsItem.date || undefined,
        url: newsItem.url || undefined,
        author: (raw?.author as string) || undefined,
        description: (raw?.description as string) || (raw?.excerpt as string) || undefined,
        locationCreated: (raw?.location as string) || (raw?.eventlocation as string) || undefined
      };
    });

    return generateNewsItemListSchema(structuredDataConfig, newsSchema);
  }, [transformedResults, coveoState.loading, coveoState.results, structuredDataConfig]);

  // Inject structured data into document head
  useStructuredData(structuredDataSchema, !coveoState.loading && !coveoState.error);

  const SearchAndFilterContent = () => (
    <>
      <div className="listing__search-wrapper">
        <SearchBoxComponent
          onSearch={handleSearchSubmit}
          initialValue={coveoState.query}
          labels={{
            searchLabel: searchLabels.searchLabel,
            searchPlaceholder: searchLabels.searchPlaceholder,
            clearLabel: searchLabels.clearLabel,
            submitLabel: searchLabels.submitLabel
          }}
        />
      </div>
      <div className="listing__facets-wrapper">
        <h2 className="listing__filter-title">{labels.filterResultsTitle || 'FILTER RESULTS'}</h2>
        {displayedFacets.length > 0 ? (
          displayedFacets.map(facet => {
            // Only render facets that have values
            if (facet.values.length === 0) {
              return null;
            }

            return (
              <FacetComponent
                key={facet.field}
                facetData={facet}
                onFilterChange={handleFacetChange}
                showMoreLabel={labels.showMoreLabel}
                showLessLabel={labels.showLessLabel}
                initialItemsToShow={facetOptionsNumber}
              />
            );
          })
        ) : (
          <div className="listing__no-facets">
            <p>{labels.noFacets || 'No filters available for this search.'}</p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <section className="listing container" aria-label="News Listing" ref={listingSectionRef}>
      <div className="listing__container">
        <div className="listing__content">
          {!isMobile && (
            <div className="listing__left-wrapper col-12 col-md-4">
              <SearchAndFilterContent />
            </div>
          )}
          <div className="listing__right-wrapper col-12 col-md-8">
            <div className="listing__actions">
              <div className="listing__actions-top">
                <Summary
                  totalResults={coveoState.totalResults}
                  labels={{
                    showingResults: labels.showingResults,
                    ofResults: labels.ofResults,
                    results: labels.results,
                    forTerm: labels.forTerm,
                    noResults: labels.noResults,
                    loading: labels.loading
                  }}
                  currentPage={coveoState.currentPage}
                  resultsPerPage={numberOfResults}
                  searchQuery={coveoState.query}
                  firstSearchExecuted={coveoState.firstSearchExecuted}
                  isLoading={coveoState.loading}
                  hasResults={coveoState.hasResults}
                />
                <div className="listing__sort">
                  <Sort
                    sortBy={coveoState.currentSort}
                    sortFields={sortFields}
                    labels={{ sortLabel: labels.sortLabel, relevanceSort: labels.relevanceSort }}
                    onSortChange={handleSortChange}
                  />
                </div>
              </div>
              {selectedFilterValues.length > 0 && (
                <div className="listing__actions-bottom">
                  <SelectedFiltersComponent
                    filters={selectedFilterValues}
                    onRemoveFilter={(field, val) => handleRemoveSelectedFilter(field, val)}
                    onClearAll={handleClearAllFilters}
                    labels={{
                      clearAll: labels.clearAll,
                      removeFilter: labels.removeFilter
                    }}
                  />
                </div>
              )}
            </div>
            <div className="listing__results">
              {coveoState.loading ? (
                <div className="listing__loading">
                  <p>{labels.loading || 'Loading...'}</p>
                </div>
              ) : coveoState.error ? (
                <div className="listing__error">
                  <p>
                    {labels.error || 'Error:'} {coveoState.error}
                  </p>
                  <p>
                    {labels.errorMessage ||
                      'There was an error loading the news listing. Please try again.'}
                  </p>
                </div>
              ) : transformedResults.length > 0 ? (
                transformedResults.map(item => (
                  <NewsResultCard
                    key={item.id}
                    newsType={item.type}
                    title={item.title}
                    date={item.date}
                    url={item.url}
                    result={item.originalResult || ({ raw: {} } as Result)}
                  />
                ))
              ) : (
                coveoState.firstSearchExecuted && (
                  <div className="listing__no-results">
                    <p>{labels.noResults || 'No content found. Please try a different search.'}</p>
                  </div>
                )
              )}
            </div>
            <div className="listing__pagination-container">
              {coveoState.totalPages > 1 && (
                <PaginationComponent
                  currentPage={coveoState.currentPage}
                  totalPages={coveoState.totalPages}
                  onPageChange={handlePageChange}
                  labels={{
                    next: labels.paginationNext || 'Next',
                    previous: labels.paginationPrevious || 'Previous',
                    page: labels.paginationPage || 'Page',
                    ellipsis: labels.paginationEllipsis || '...'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isMobile && displayedFacets.length > 0 && (
        <button
          className="listing__filter-search-button"
          onClick={() => setIsFilterDialogOpen(true)}
        >
          {labels.filterAndSearchButton || 'Filter & Search'} <ChevronUp />
        </button>
      )}
      {isMobile && displayedFacets.length > 0 && (
        <FilterDialog
          isOpen={isFilterDialogOpen}
          onClose={() => setIsFilterDialogOpen(false)}
          onSearch={handleSearchSubmit}
          initialSearchTerm={coveoState.query}
          facets={displayedFacets
            .filter(f => f.values.length > 0)
            .map(f => ({
              label: f.title,
              field: f.field,
              expanded: f.expanded
            }))}
          generateFacetData={(facetOpt, _newsData, selFacets) => {
            const facetData = displayedFacets.find(f => f.field === facetOpt.field);
            if (facetData) {
              return {
                ...facetData,
                title: facetOpt.label,
                expanded: true,
                values: facetData.values.map(v => ({
                  ...v,
                  selected: (selFacets[facetOpt.field] ?? []).includes(v.label)
                }))
              };
            }
            return {
              title: facetOpt.label,
              field: facetOpt.field,
              expanded: true,
              values: []
            };
          }}
          results={coveoState.results as unknown as Person[]}
          selectedFacets={Object.fromEntries(
            Object.entries(coveoState.facets)
              .map(([field, values]) => [
                field,
                values.filter(v => v.state === 'selected').map(v => v.value)
              ])
              .filter(([, values]) => values.length > 0)
          )}
          onFilterChange={(field, val, checked) => handleFacetChange(field, val, checked)}
          onClearAllFilters={handleClearAllFilters}
          labels={labels}
          searchLabels={searchLabels}
          facetOptionsNumber={facetOptionsNumber}
        />
      )}
    </section>
  );
};

export default NewsListing;
